import { normalizeChoices } from '@/utils'

/**
 * Creates a deterministic key for grouping products that includes nested options.
 * Optimized to avoid expensive JSON.stringify by building a string key from IDs only.
 * This ensures products with the same base options but different nested options are grouped separately.
 * @param {Array} normalizedOptions - The normalized options array
 * @returns {string} - A deterministic string key for grouping
 */
function createGroupingKey(normalizedOptions) {
	if (!normalizedOptions || !Array.isArray(normalizedOptions) || normalizedOptions.length === 0) {
		return ''
	}

	// Build key from IDs only, avoiding full object serialization
	return normalizedOptions
		.map(option => {
			const optId = option.optionId || option.id || ''
			const chosenIds = option.chosenIds || option.options || []
			
			// Process chosen IDs to create a deterministic string
		const chosenStr = chosenIds
			.map(chosenId => {
				if (typeof chosenId === 'string') {
					return chosenId
				}
				if (typeof chosenId === 'object' && chosenId) {
					const id = chosenId.id || ''
					const commentStr = chosenId.comment || ''
					// Include nested options in the key
					const nestedStr = Array.isArray(chosenId.options)
						? chosenId.options
							.map(n => typeof n === 'string' ? n : (n?.id || ''))
							.sort()
							.join(',')
						: ''
					let key = nestedStr ? `${id}:(${nestedStr})` : id
					if (commentStr) {
						key += `[${commentStr}]`
					}
					return key
				}
				return String(chosenId)
			})
				.sort()
				.join('|')
			
			return `${optId}=${chosenStr}`
		})
		.sort()
		.join(';')
}

/**
 * Shallow clone a choice object (only clones the immediate structure, not deep nested objects).
 * This is faster than JSON.parse(JSON.stringify()) for simple structures.
 * @param {any} choice - The choice to clone
 * @returns {any} - A cloned copy
 */
function cloneChoice(choice) {
	if (typeof choice !== 'object' || choice === null) {
		return choice
	}
	if (Array.isArray(choice)) {
		return choice.map(cloneChoice)
	}
	// Clone object with its immediate properties
	const cloned = {}
	for (const key in choice) {
		if (Object.prototype.hasOwnProperty.call(choice, key)) {
			const value = choice[key]
			// For options array, do a shallow clone of items
			if (key === 'options' && Array.isArray(value)) {
				cloned[key] = value.map(opt => 
					typeof opt === 'object' && opt !== null ? { ...opt } : opt
				)
			} else {
				cloned[key] = value
			}
		}
	}
	return cloned
}

// Function to expand breakfast/menu products into separate products
function expandBreakfastAndMenuProducts(productsCart) {
	const expandedProducts = []

	productsCart.forEach(product => {
		if ((product.bulkSelectable || product.isMenu === "SI") && product.cartItemCount > 1) {
			const count = product.cartItemCount
			const chosenOptions = product.chosenOptions || []
			for (let i = 0; i < count; i++) {
				const itemOptions = chosenOptions.map(option => {
					const chosenIds = Array.isArray(option.chosenIds) ? option.chosenIds : []
					if (chosenIds.length === 0) return null

					// si no hay elegido en posición i, reutilizamos ciclando las opciones disponibles
					let selectedChoice = chosenIds[i]
					if (selectedChoice === undefined) {
						// If we have choices but not enough for this index, cycle through them
						selectedChoice = chosenIds.length > 0 ? chosenIds[i % chosenIds.length] : undefined
					}
					if (selectedChoice === undefined) return null

					const clonedSelectedChoice = cloneChoice(selectedChoice)
					return {
						optionId: option.optionId,
						chosenIds: [clonedSelectedChoice]
					}
				}).filter(option => option !== null)

				expandedProducts.push({
					...product,
					cartItemCount: 1,
					chosenOptions: itemOptions
				})
			}
		} else {
			expandedProducts.push(product)
		}
	})
	return expandedProducts
}

// Function to group products by SKU + options + units for new format
export function groupProductsBySkuAndOptions(productsCart) {
	const groupedProducts = new Map()

	// Primero expandir los breakfasts y menús en productos separados
	const expandedCart = expandBreakfastAndMenuProducts(productsCart)

	expandedCart.forEach(product => {
		// Create a unique key based on SKU + selling format + normalized options + units (for unitary products)
		// Different selling formats of the same product (e.g. "Unidad" vs "Ración") must never be merged together.
		const normalizedOptions = normalizeChoices(product.chosenOptions)
		const units = product.isUnitary === 'SI' ? product.cartItemCount : null
		const sellingFormatId = product.sellingFormatId || 'no-format'
		const key = `${product.sku}_${sellingFormatId}_${createGroupingKey(normalizedOptions)}_${units || 'non-unitary'}`

		if (groupedProducts.has(key)) {
			const existing = groupedProducts.get(key)
			const count = product.cartItemCount || 1

			// Para productos unitarios: incrementar units, orderQuantity se mantiene en 1
			// Para productos normales: incrementar orderQuantity
			if (product.isUnitary === 'SI') {
				existing.units = (existing.units || 0) + count
				// orderQuantity se mantiene en 1 para unitarios
			} else {
				existing.orderQuantity += count
			}

			// Merge comments if they exist (array format with {comment, count})
			if (product.comments && product.comments.length > 0) {
				if (!existing.staffComments) {
					existing.staffComments = []
				}
				product.comments.forEach(c => {
					const commentText = typeof c === 'string' ? c : c.comment
					if (commentText) {
						// Buscar si ya existe el mismo comentario para incrementar count
						const existingComment = existing.staffComments.find(sc => sc.comment === commentText)
						if (existingComment) {
							existingComment.count += 1
						} else {
							existing.staffComments.push({ comment: commentText, count: 1 })
						}
					}
				})
			}
			// Also handle single comment field (from FeatureCommander)
			if (product.comment && typeof product.comment === 'string' && product.comment.trim()) {
				if (!existing.staffComments) {
					existing.staffComments = []
				}
				const commentText = product.comment.trim()
				// Buscar si ya existe el mismo comentario para incrementar count
				const existingComment = existing.staffComments.find(sc => sc.comment === commentText)
				if (existingComment) {
					existingComment.count += 1
				} else {
					existing.staffComments.push({ comment: commentText, count: 1 })
				}
			}
		} else {
			// Transform chosenOptions to ensure nested options have the correct structure
			const transformedOptions = Array.isArray(product.chosenOptions)
				? product.chosenOptions.map(option => ({
					optionId: option.optionId,
					chosenIds: Array.isArray(option.chosenIds)
						? option.chosenIds.map(chosenId => {
							// If it's just a string, return as is
							if (typeof chosenId === 'string') {
								return chosenId
							}
						// If it's an object with nested options
						if (typeof chosenId === 'object' && chosenId.id) {
							const entry = {
								id: chosenId.id,
								options: Array.isArray(chosenId.options)
									? chosenId.options.map(nestedOption => {
										// Flatten nested structure - extract just the ID
										if (typeof nestedOption === 'string') {
											return { id: nestedOption }
										}
										if (typeof nestedOption === 'object' && nestedOption.id) {
											// If double-nested, extract the inner ID
											const actualId = typeof nestedOption.id === 'string'
												? nestedOption.id
												: nestedOption.id.id || nestedOption.id
											return { id: actualId }
										}
										return nestedOption
									})
									: []
							}
							if (chosenId.comment) {
								entry.comment = chosenId.comment
							}
							return entry
						}
							return chosenId
						})
						: []
				}))
				: product.chosenOptions

			// Para productos unitarios: orderQuantity=1, units=cantidad
			// Para productos normales: orderQuantity=cantidad
			const isUnitary = product.isUnitary === 'SI'
			const count = product.cartItemCount || 1

			const groupedProduct = {
				sku: product.sku,
				options: transformedOptions,
				orderQuantity: isUnitary ? 1 : count,
				modifyDishClass: product.modifyDishClass?.id,
				isShareable: product.isShareable
			}

			// Selling format: required by the backend whenever the product has enabled formats.
			if (product.sellingFormatId) {
				groupedProduct.sellingFormatId = product.sellingFormatId
			}

			// Add units field only for unitary products
			if (isUnitary) {
				groupedProduct.units = count
			}

			// Add staff comments if they exist (array format with {comment, count})
			if (product.comments && product.comments.length > 0) {
				groupedProduct.staffComments = []
				product.comments.forEach(c => {
					const commentText = typeof c === 'string' ? c : c.comment
					if (commentText) {
						groupedProduct.staffComments.push({ comment: commentText, count: 1 })
					}
				})
			}
			// Also handle single comment field (from FeatureCommander)
			if (product.comment && typeof product.comment === 'string' && product.comment.trim()) {
				if (!groupedProduct.staffComments) {
					groupedProduct.staffComments = []
				}
				groupedProduct.staffComments.push({ comment: product.comment.trim(), count: 1 })
			}

			groupedProducts.set(key, groupedProduct)
		}
	})

	return Array.from(groupedProducts.values())
}
